/**
 * Receipt Deductions Helper — per-invoice WHT + Adjustment / Bad Debt on
 * Bank / Cash Receipts (BR / CR).
 *
 * Each invoice allocation on a receipt may carry:
 *   allocatedAmount   — GROSS amount cleared from the invoice (invoice goes Paid)
 *   whtRate/whtAmount — income tax withheld by the customer → Dr 11205001
 *   adjustmentAmount  — amount written off (bad debt / settlement adj.) → Dr Bad Debts
 * Cash actually received for that invoice = allocatedAmount − whtAmount − adjustmentAmount.
 *
 * Posting (built by the receipt form as manual lines):
 *   Dr Bank/Cash (net cash), Dr WHT Receivable (per invoice), Dr Bad Debts
 *   (per invoice), Cr Customer (gross).
 */

import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getAccountsCached } from './masterDataStore';
import { SYSTEM_ACCOUNTS } from '../config/tranTypes';

// Fixed seed account added by Repair COA (see defaultChartOfAccounts, createOnly).
// Deliberately NOT in SYSTEM_ACCOUNTS: a brand-new code there would make every
// document save in every company run a live "missing system account" lookup
// until that company ran Repair COA.
export const BAD_DEBT_ACCOUNT_CODE = '52116001';

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/** Σ gross / WHT / adjustment over a list of allocations. */
export const sumAllocationDeductions = (allocations = []) => {
  let gross = 0, wht = 0, adj = 0;
  for (const a of allocations || []) {
    gross += Number(a.allocatedAmount) || 0;
    wht += Number(a.whtAmount) || 0;
    adj += Number(a.adjustmentAmount) || 0;
  }
  return { gross: round2(gross), wht: round2(wht), adj: round2(adj) };
};

/**
 * GROSS amount a Bank/Cash Receipt cleared from the customer(s) =
 * cash received (totalAmount) + WHT + adjustment / bad debt.
 *
 * Always DERIVE it — never store/read `grossAmount` for this: transactionHelper
 * reserves `grossAmount` as an alias of the item subtotal and overwrites it on
 * every create/update (receipts have no items → it is always 0). Old receipts
 * (no deductions) give back exactly their totalAmount.
 */
export const receiptGross = (rec) =>
  round2((Number(rec?.totalAmount) || 0) + (Number(rec?.whtAmount) || 0) + (Number(rec?.adjustmentAmount) || 0));

const isPostable = (a) => (a.level === 5 || a.isPostable === true) && a.isActive !== false;
const byCode = (a, b) => String(a.code || '').localeCompare(String(b.code || ''));

/**
 * The company's Bad Debt account: its OWN postable expense account named
 * "Bad Debt(s)" first (they may have made one long ago), else the fixed
 * 52116001 added by Repair COA. Excludes "…Recovered" income-style names.
 */
export const findBadDebtAccount = (accounts = []) => {
  const candidates = accounts.filter(a =>
    isPostable(a) &&
    String(a.accountType || '').toLowerCase() === 'expense' &&
    /bad\s*debts?/i.test(a.name || '') &&
    !/recover/i.test(a.name || '')
  );
  const own = candidates.filter(a => a.code !== BAD_DEBT_ACCOUNT_CODE).sort(byCode);
  if (own.length) return own[0];
  return candidates.find(a => a.code === BAD_DEBT_ACCOUNT_CODE) || null;
};

const REPAIR_HINT = 'Open Master Data → Chart of Accounts → click "Repair COA", then try again.';

/**
 * Resolve the WHT Receivable and/or Bad Debt accounts for a receipt save.
 * Uses the device-cached account list (0 reads); only if an account is not in
 * the cache (e.g. Repair COA was just run on another device) does it make ONE
 * small live lookup by code. Throws a plain-language error when missing.
 */
export const resolveReceiptDeductionAccounts = async (companyId, { needWht, needBadDebt }) => {
  if (!needWht && !needBadDebt) return { whtAccount: null, badDebtAccount: null };

  let accounts = [];
  try { accounts = (await getAccountsCached(companyId)) || []; } catch (e) { accounts = []; }

  let wht = needWht ? accounts.find(a => a.code === SYSTEM_ACCOUNTS.WHT_RECEIVABLE && a.isActive !== false) : null;
  let bad = needBadDebt ? findBadDebtAccount(accounts) : null;

  const missingCodes = [];
  if (needWht && !wht) missingCodes.push(SYSTEM_ACCOUNTS.WHT_RECEIVABLE);
  if (needBadDebt && !bad) missingCodes.push(BAD_DEBT_ACCOUNT_CODE);
  if (missingCodes.length) {
    try {
      const snap = await getDocs(query(
        collection(db, 'companies', companyId, 'accounts'),
        where('code', 'in', missingCodes)
      ));
      snap.docs.forEach(d => {
        const acc = { id: d.id, ...d.data() };
        if (acc.isActive === false) return;
        if (acc.code === SYSTEM_ACCOUNTS.WHT_RECEIVABLE && needWht && !wht) wht = acc;
        if (acc.code === BAD_DEBT_ACCOUNT_CODE && needBadDebt && !bad) bad = acc;
      });
    } catch (e) { /* fall through to the clear error below */ }
  }

  if (needWht && !wht) {
    throw new Error(`The WHT Receivable account (${SYSTEM_ACCOUNTS.WHT_RECEIVABLE}) is missing from your Chart of Accounts. ${REPAIR_HINT}`);
  }
  if (needBadDebt && !bad) {
    throw new Error(`No Bad Debts account found in your Chart of Accounts. ${REPAIR_HINT} (Or create an expense account named "Bad Debts".)`);
  }
  return {
    whtAccount: wht ? { id: wht.id, code: wht.code || '', name: wht.name || 'WHT Receivable' } : null,
    badDebtAccount: bad ? { id: bad.id, code: bad.code || '', name: bad.name || 'Bad Debts' } : null
  };
};

/**
 * Debit legs for the deductions — ONE WHT line and ONE bad-debt line PER
 * INVOICE (so the 11205001 ledger lines up with each WHT certificate).
 */
export const buildDeductionLines = (allocations, { whtAccount, badDebtAccount }, partyLabel) => {
  const lines = [];
  for (const a of allocations || []) {
    const wht = round2(a.whtAmount);
    const adj = round2(a.adjustmentAmount);
    const ref = a.docNumber || 'invoice';
    if (wht > 0 && whtAccount) {
      lines.push({
        accountId: whtAccount.id,
        accountCode: whtAccount.code,
        accountName: whtAccount.name,
        debitAmount: wht,
        creditAmount: 0,
        description: `WHT${Number(a.whtRate) > 0 ? ` @${Number(a.whtRate)}%` : ''} deducted by ${partyLabel || 'customer'} on ${ref}`
      });
    }
    if (adj > 0 && badDebtAccount) {
      lines.push({
        accountId: badDebtAccount.id,
        accountCode: badDebtAccount.code,
        accountName: badDebtAccount.name,
        debitAmount: adj,
        creditAmount: 0,
        description: `Bad debt / adjustment on ${ref} (${partyLabel || 'customer'})`
      });
    }
  }
  return lines;
};

/**
 * Invoices where WHT is entered on the receipt although the Sale Invoice ITSELF
 * already deducted WHT ("deduct" direction) — entering it again double-counts
 * WHT Receivable. Returns the invoice numbers to warn about.
 */
export const findInvoiceLevelWhtConflicts = (allocations, invoices) =>
  (allocations || [])
    .filter(a => (Number(a.whtAmount) || 0) > 0)
    .map(a => (invoices || []).find(i => i.id === a.invoiceId))
    .filter(inv => inv && Number(inv.whtAmount) > 0 && inv.whtAddToTotal !== true)
    .map(inv => inv.docNumber);

/* ════════════════════════════════════════════════════════════════════════
 * PAYMENTS (Bank / Cash Payment — BP / CP)
 *
 * Same per-invoice columns as receipts, mirrored:
 *   allocatedAmount  — GROSS cleared from the vendor invoice (invoice goes Paid)
 *   whtAmount        — tax WE withhold from the vendor → Cr WHT Payable 21203001
 *   adjustmentAmount — amount the vendor lets us short-pay → Cr Purchase Discount 51103001
 * Plus ONE payment-level "advance WHT" (whtRate / whtAmount on the form) that
 * applies only to the UNALLOCATED part (advance payments, legacy header WHT).
 *
 * Posting: Dr Vendor (gross), Cr Bank/Cash (net), Cr WHT Payable (per invoice +
 * advance), Cr Purchase Discount (per invoice).
 *
 * A payment's totalAmount stays GROSS (its historical meaning). Net is ALWAYS
 * derived — transactionHelper overwrites the stored `netAmount` with totalAmount.
 * ════════════════════════════════════════════════════════════════════════ */

/** Net cash paid on a Bank/Cash Payment = gross − all WHT − adjustment. */
export const paymentNet = (rec) =>
  round2((Number(rec?.totalAmount) || 0) - (Number(rec?.whtAmount) || 0) - (Number(rec?.adjustmentAmount) || 0));

/** Advance WHT on the part of `gross` NOT allocated to any invoice. */
export const fitAdvanceWht = (gross, allocations, rate, currentAmount = 0) => {
  const unallocated = Math.max(0, round2((Number(gross) || 0) - sumAllocationDeductions(allocations).gross));
  if ((Number(rate) || 0) > 0) return round2(unallocated * (Number(rate) / 100));
  return round2(Math.min(Math.max(0, Number(currentAmount) || 0), unallocated));
};

/**
 * Recompute a multi-party PAYMENT line: invoice WHT / adjustment come from its
 * allocation rows, advance WHT (line.whtRate) applies to its unallocated part.
 * line.whtAmount = TOTAL WHT of the line (invoice + advance).
 */
export const recalcPaymentLine = (line) => {
  const amount = Number(line.amount) || 0;
  const ded = sumAllocationDeductions(line.invoiceAllocations);
  const advanceWhtAmount = fitAdvanceWht(amount, line.invoiceAllocations, line.whtRate, line.advanceWhtAmount);
  const whtAmount = round2(ded.wht + advanceWhtAmount);
  return {
    ...line,
    advanceWhtAmount,
    whtAmount,
    adjustmentAmount: ded.adj,
    netAmount: round2(amount - whtAmount - ded.adj)
  };
};

/**
 * Resolve WHT Payable (21203001) and/or Purchase Discount (51103001) for a
 * payment save — cached list (0 reads), ONE small live lookup only if missing.
 */
export const resolvePaymentDeductionAccounts = async (companyId, { needWht, needAdj }) => {
  if (!needWht && !needAdj) return { whtAccount: null, adjAccount: null };

  let accounts = [];
  try { accounts = (await getAccountsCached(companyId)) || []; } catch (e) { accounts = []; }
  const byCodeActive = (code) => accounts.find(a => a.code === code && a.isActive !== false) || null;

  let wht = needWht ? byCodeActive(SYSTEM_ACCOUNTS.WHT_PAYABLE) : null;
  let adj = needAdj ? byCodeActive(SYSTEM_ACCOUNTS.PURCHASE_DISCOUNT) : null;

  const missingCodes = [];
  if (needWht && !wht) missingCodes.push(SYSTEM_ACCOUNTS.WHT_PAYABLE);
  if (needAdj && !adj) missingCodes.push(SYSTEM_ACCOUNTS.PURCHASE_DISCOUNT);
  if (missingCodes.length) {
    try {
      const snap = await getDocs(query(
        collection(db, 'companies', companyId, 'accounts'),
        where('code', 'in', missingCodes)
      ));
      snap.docs.forEach(d => {
        const acc = { id: d.id, ...d.data() };
        if (acc.isActive === false) return;
        if (acc.code === SYSTEM_ACCOUNTS.WHT_PAYABLE && needWht && !wht) wht = acc;
        if (acc.code === SYSTEM_ACCOUNTS.PURCHASE_DISCOUNT && needAdj && !adj) adj = acc;
      });
    } catch (e) { /* fall through to the clear error below */ }
  }

  if (needWht && !wht) {
    throw new Error(`The WHT Payable account (${SYSTEM_ACCOUNTS.WHT_PAYABLE}) is missing from your Chart of Accounts. ${REPAIR_HINT}`);
  }
  if (needAdj && !adj) {
    throw new Error(`The Purchase Discount account (${SYSTEM_ACCOUNTS.PURCHASE_DISCOUNT}) is missing from your Chart of Accounts. ${REPAIR_HINT}`);
  }
  return {
    whtAccount: wht ? { id: wht.id, code: wht.code || '', name: wht.name || 'WHT Payable' } : null,
    adjAccount: adj ? { id: adj.id, code: adj.code || '', name: adj.name || 'Purchase Discount' } : null
  };
};

/**
 * Credit legs for a payment's per-invoice deductions — ONE WHT line and ONE
 * adjustment line PER INVOICE (WHT Payable ledger lines up per invoice).
 */
export const buildPaymentDeductionLines = (allocations, { whtAccount, adjAccount }, partyLabel) => {
  const lines = [];
  for (const a of allocations || []) {
    const wht = round2(a.whtAmount);
    const adj = round2(a.adjustmentAmount);
    const ref = a.docNumber || 'invoice';
    if (wht > 0 && whtAccount) {
      lines.push({
        accountId: whtAccount.id,
        accountCode: whtAccount.code,
        accountName: whtAccount.name,
        debitAmount: 0,
        creditAmount: wht,
        description: `WHT${Number(a.whtRate) > 0 ? ` @${Number(a.whtRate)}%` : ''} withheld from ${partyLabel || 'vendor'} on ${ref}`
      });
    }
    if (adj > 0 && adjAccount) {
      lines.push({
        accountId: adjAccount.id,
        accountCode: adjAccount.code,
        accountName: adjAccount.name,
        debitAmount: 0,
        creditAmount: adj,
        description: `Adjustment / discount on ${ref} (${partyLabel || 'vendor'})`
      });
    }
  }
  return lines;
};

/** Credit leg for the advance WHT on a payment's unallocated part. */
export const buildAdvanceWhtLine = (amount, rate, whtAccount, partyLabel) => {
  const wht = round2(amount);
  if (!(wht > 0) || !whtAccount) return null;
  return {
    accountId: whtAccount.id,
    accountCode: whtAccount.code,
    accountName: whtAccount.name,
    debitAmount: 0,
    creditAmount: wht,
    description: `WHT${Number(rate) > 0 ? ` @${Number(rate)}%` : ''} withheld from ${partyLabel || 'vendor'} (advance / unallocated)`
  };
};

/**
 * Legacy payment → per-invoice layout, on EDIT LOAD only.
 * Old payments (no `advanceWhtAmount` field) kept all WHT in one payment/line-level
 * field at `legacyRate` on the whole amount. Move that rate onto the invoice rows
 * so re-allocating keeps WHT on them (otherwise raising an allocation would drop
 * WHT to 0), and leave the remainder as advance WHT. Total WHT is unchanged, so a
 * plain re-save posts the same amounts.
 * Returns { allocations, advanceWhtAmount }.
 */
export const migrateLegacyPaymentWht = (allocations, legacyRate, legacyWhtAmount) => {
  const allocs = allocations || [];
  const rate = Number(legacyRate) || 0;
  const total = Number(legacyWhtAmount) || 0;
  const rowsHaveWht = allocs.some(a => Number(a.whtAmount) > 0);
  if (!(rate > 0) || rowsHaveWht || total <= 0 || allocs.length === 0) {
    return { allocations: allocs, advanceWhtAmount: round2(Math.max(0, total - sumAllocationDeductions(allocs).wht)) };
  }
  let remaining = total;
  const migrated = allocs.map(a => {
    const w = Math.min(round2((Number(a.allocatedAmount) || 0) * rate / 100), round2(remaining));
    remaining = round2(remaining - w);
    return { ...a, whtRate: rate, whtAmount: w, adjustmentAmount: Number(a.adjustmentAmount) || 0 };
  });
  return { allocations: migrated, advanceWhtAmount: round2(Math.max(0, remaining)) };
};
